/**
 * 渠道中心 API · 真契约源 apps/api-server/src/api_server/routes/integrations_admin.py
 */
import { api } from "./api";
import type {
  CapabilityRead,
  MappingProductRow,
  MappingTierRow,
  PlatformCapabilities,
  PlatformDetail,
  PlatformSettingsResult,
  PlatformStatus,
  PlatformSummary,
  ListingPlatform,
  RotateCredentialsResult,
  SeatDetail,
  SyncAttemptEntry,
  SyncLogEntry,
  TestConnectionResult,
} from "./types/channelCenter";

const BASE = "/api/v1/integrations";

export function listPlatforms() {
  return api<PlatformSummary[]>(`${BASE}/platforms`);
}

/**
 * POST /platforms · 新增平台连接（PLATNEW·2026-09-10）
 *
 * 🔴 **不传任何凭据**——入站走 rotateInboundCredentials()，出站走 updateOutboundCredentials()，
 * 都是既有的专用接口。新建只落一行配置，建完 status=inactive，要手动开。
 */
export interface PlatformCatalogEntry {
  code: string;
  label: string;
  note: string;
  already_added: boolean;
  capabilities: PlatformCapabilities;
}

/** GET /platform-catalog · 「新增平台」下拉的唯一真源（已接适配器的平台 + 本租户是否已建）*/
export function getPlatformCatalog() {
  return api<PlatformCatalogEntry[]>(`${BASE}/platform-catalog`);
}

export function createPlatform(payload: {
  code: string;                       // 必须是目录里的 code（用户裁决 A：白名单）
  display_name?: string | null;       // 留空用目录默认名
  platform_supplier_id?: string | null;
  outbound_base_url?: string | null;
}) {
  return api<PlatformDetail>(`${BASE}/platforms`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getPlatform(code: string) {
  return api<PlatformDetail>(`${BASE}/platforms/${code}`);
}

export function updatePlatformStatus(code: string, status: PlatformStatus) {
  return api<PlatformDetail>(`${BASE}/platforms/${code}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

/**
 * PATCH /platforms/{code}/settings · 改显示名 / 平台供应商 ID / 出站根地址（PLATEDIT-1）
 * 只改传了的字段；outbound_base_url / platform_supplier_id 传 "" = 清空。不收任何凭据。
 */
export function updatePlatformSettings(
  code: string,
  payload: { display_name?: string; platform_supplier_id?: string; outbound_base_url?: string },
) {
  return api<PlatformSettingsResult>(`${BASE}/platforms/${code}/settings`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/**
 * PATCH /platforms/{code}/allow-pending · 「接口待确认」开关（KLOOKPEND-1 · 只有老板 / 技术管理员）。
 * 打开 = 不满足自动确认条件的订单回 PENDING 转人工；关闭 = 一律即时确认（现状）。reason 选填，进操作日志。
 */
export function updateAllowPending(code: string, payload: { enabled: boolean; reason?: string }) {
  return api<PlatformDetail>(`${BASE}/platforms/${code}/allow-pending`, { method: "PATCH", body: JSON.stringify(payload) });
}

/** GET /platforms/{code}/mappings/{product_id}/seat-detail · 渠道余位明细（CHANAV-3 · 只读） */
export function getSeatDetail(code: string, productId: string, days = 30) {
  return api<SeatDetail>(`${BASE}/platforms/${code}/mappings/${productId}/seat-detail?days=${days}`);
}

/** POST /platforms/{code}/inbound-credentials:rotate · 不传 label = 重铸主凭据；label="live" = 重铸（首次则新建）Bokun 正式账号这套额外凭据（BOK-2） */
export function rotateInboundCredentials(code: string, label?: "live") {
  return api<RotateCredentialsResult>(`${BASE}/platforms/${code}/inbound-credentials:rotate`, {
    method: "POST",
    ...(label ? { body: JSON.stringify({ label }) } : {}),
  });
}

export function updateOutboundCredentials(code: string, username: string, password: string) {
  return api<{ ok: true }>(`${BASE}/platforms/${code}/outbound-credentials`, {
    method: "PUT",
    body: JSON.stringify({ username, password }),
  });
}

export function testConnection(code: string, direction: "inbound" | "outbound") {
  return api<TestConnectionResult>(`${BASE}/platforms/${code}/test`, {
    method: "POST",
    body: JSON.stringify({ direction }),
  });
}

export function getPlatformHealth(code: string) {
  return api<CapabilityRead[]>(`${BASE}/platforms/${code}/health`);
}

export function getGlobalSyncLogs(params: { platform?: string; status?: string; page?: number; pageSize?: number }) {
  const q = new URLSearchParams();
  if (params.platform) q.set("platform", params.platform);
  if (params.status) q.set("status", params.status);
  q.set("page", String(params.page ?? 1));
  q.set("page_size", String(params.pageSize ?? 50));
  return api<SyncLogEntry[]>(`${BASE}/sync-logs?${q.toString()}`, { paged: true });
}

export function getPlatformSyncLogs(code: string, page = 1, pageSize = 20) {
  return api<SyncLogEntry[]>(`${BASE}/platforms/${code}/sync-logs?page=${page}&page_size=${pageSize}`, { paged: true });
}

/** GYG-LOG-1 · 某条同步日志事件的逐次请求 / 返回（只读，老板 / 技术管理员） */
export function getSyncLogAttempts(eventId: string) {
  return api<SyncAttemptEntry[]>(`${BASE}/sync-logs/${eventId}/attempts`);
}

export function retrySyncLog(eventId: string, force = false) {
  return api<{ ok: true; event_id: string; status: string }>(`${BASE}/sync-logs/${eventId}:retry`, {
    method: "POST",
    body: JSON.stringify({ force }),
  });
}

export function listMappings(code: string) {
  return api<MappingProductRow[]>(`${BASE}/platforms/${code}/mappings`, { paged: true });
}

/** OPT-B2b · optionId = 连哪个选项（不传 = 默认选项，老调用不变） */
export function createMapping(code: string, productId: string, optionId?: string) {
  return api<MappingProductRow>(`${BASE}/platforms/${code}/mappings`, {
    method: "POST",
    body: JSON.stringify(optionId ? { product_id: productId, option_id: optionId } : { product_id: productId }),
  });
}

/** LISTCTR-1 · 上架中心读平台列表：只读精简口子（运营可进，不含凭据）；渠道中心用 listPlatforms（仅老板 / 技术管理员） */
export function listListingPlatforms() {
  return api<ListingPlatform[]>("/api/v1/listing/platforms");
}

export function patchMapping(
  code: string,
  mappingId: string,
  body: { status?: "active" | "inactive"; platform_bound?: boolean; category_map?: Record<string, Record<string, never>> },
) {
  return api<MappingTierRow>(`${BASE}/platforms/${code}/mappings/${mappingId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** CHANAV-2 E · 打开 / 关闭产品的「满车加售」：按每个启用中的常规档生成 / 停用 -X 兄弟映射（幂等） */
export function setExtraVehicle(code: string, productId: string, enabled: boolean) {
  return api<MappingProductRow>(`${BASE}/platforms/${code}/mappings/${productId}:extra-vehicle`, {
    method: "POST",
    body: JSON.stringify({ enabled }),
  });
}

export function publishMapping(code: string, productId: string) {
  return api<MappingProductRow>(`${BASE}/platforms/${code}/mappings:publish`, {
    method: "POST",
    body: JSON.stringify({ product_id: productId }),
  });
}

/**
 * LISTSW-1 · 「勾选产品上哪个平台」的唯一入口——上架中心的勾选框和产品详情「上架到哪些平台」开关都调它，
 * 读写的是同一份 external_product_mappings（不另建产品级字段）。
 *
 * - 开：还没映射 → createMapping（后端幂等，含 R1~R4 档）；已映射 → 把各档 patch 成 active
 * - 关：把各档 patch 成 inactive（停用后不再向该平台推送余位，平台上的商品不会被删除）
 * 后端映射接口不查平台是否已开闸（开闸只管能不能卖），所以平台没开闸时也能先勾选。
 *
 * CHANAV-1 · 返回后端给的只读提示（去重）：挂线路的产品未来 30 天没有车次时，打开映射照常成功，
 * 响应带 warnings——调用方在开关处显示，不拦。关映射恒返回空数组。
 */
export async function toggleProductMapping(code: string, row: MappingProductRow, on: boolean): Promise<string[]> {
  let warnings: string[] = [];
  if (!row.mapped) {
    if (on) warnings = (await createMapping(code, row.product_id)).warnings ?? [];
    return warnings;
  }
  const res = await Promise.all(row.tiers.map((t) => patchMapping(code, t.mapping_id, { status: on ? "active" : "inactive" })));
  if (on) warnings = res.flatMap((r) => r?.warnings ?? []);
  return Array.from(new Set(warnings));
}

/** 映射 / 上架任务相关查询的 key 前缀——任何一处改了映射，用它们一起失效，另一处刷新后一致。 */
export const MAPPINGS_QUERY_KEY = ["channel-center", "mappings"] as const;
export const ELIGIBLE_PRODUCTS_QUERY_KEY = ["listing-jobs", "eligible-products"] as const;
